import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';
import TopNavigation from '../../components/feature/TopNavigation';

export default function CameraPage() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [detectedPlate, setDetectedPlate] = useState('');
  const [processing, setProcessing] = useState(false);
  const [cameraError, setCameraError] = useState('');

  useEffect(() => {
    startCamera();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      setCameraError('لا يمكن الوصول إلى الكاميرا. تأكد من منح الإذن للتطبيق.');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');

    if (context) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      
      const imageData = canvas.toDataURL('image/jpeg');
      setCapturedImage(imageData);
      
      // Stop camera
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      // Simulate OCR processing
      processImage();
    }
  };

  const processImage = () => {
    setProcessing(true);
    
    // Simulate OCR processing
    setTimeout(() => {
      const mockPlateNumber = 'أ ب ج 1234';
      setDetectedPlate(mockPlateNumber);
      setProcessing(false);
    }, 3000);
  };

  const confirmPlate = () => {
    navigate(`/results?plate=${encodeURIComponent(detectedPlate)}`);
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setDetectedPlate('');
    setProcessing(false);
    startCamera();
  };

  if (cameraError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopNavigation title="الكاميرا" showBack={true} />
        <div className="pt-16 px-6 py-8 flex flex-col items-center justify-center min-h-screen">
          <div className="text-center">
            <i className="ri-camera-off-line text-6xl text-gray-400 mb-4 w-24 h-24 flex items-center justify-center mx-auto"></i>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">خطأ في الكاميرا</h2>
            <p className="text-gray-600 mb-6">{cameraError}</p>
            <Button onClick={() => navigate('/search')}>
              استخدم البحث اليدوي
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <TopNavigation 
        title="مسح لوحة السيارة"
        showBack={true}
        className="bg-black/50 border-none"
      />
      
      <div className="pt-16 relative h-screen">
        {!capturedImage ? (
          <>
            {/* Camera View */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
            
            {/* Camera Overlay */}
            <div className="absolute inset-0 flex flex-col justify-between p-6">
              {/* Instructions */}
              <div className="text-center mt-8">
                <div className="bg-black/50 rounded-lg p-4 inline-block">
                  <p className="text-white text-sm">
                    وجه الكاميرا نحو لوحة السيارة
                  </p>
                </div>
              </div>

              {/* Scan Frame */}
              <div className="flex-1 flex items-center justify-center">
                <div className="relative w-80 h-32 border-2 border-white rounded-lg">
                  <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-blue-500 rounded-tl-lg"></div>
                  <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-blue-500 rounded-tr-lg"></div>
                  <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-blue-500 rounded-bl-lg"></div>
                  <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-blue-500 rounded-br-lg"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-white text-xs bg-black/50 px-2 py-1 rounded">
                      ضع اللوحة هنا
                    </p>
                  </div>
                </div>
              </div>

              {/* Capture Button */}
              <div className="text-center">
                <button
                  onClick={capturePhoto}
                  className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg"
                >
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                    <i className="ri-camera-line text-white text-2xl w-8 h-8 flex items-center justify-center"></i>
                  </div>
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="h-full bg-gray-50">
            {/* Captured Image */}
            <div className="p-6">
              <img
                src={capturedImage}
                alt="Captured plate"
                className="w-full h-64 object-cover rounded-lg mb-6"
              />

              {processing ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <i className="ri-loader-4-line animate-spin text-2xl text-blue-600 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    جارٍ معالجة الصورة...
                  </h3>
                  <p className="text-gray-600">
                    يتم استخراج رقم اللوحة تلقائياً
                  </p>
                </div>
              ) : detectedPlate ? (
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3 mx-auto">
                      <i className="ri-check-line text-green-600 text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      تم اكتشاف رقم اللوحة
                    </h3>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <p className="text-center text-2xl font-bold text-gray-900">
                      {detectedPlate}
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Button
                      onClick={confirmPlate}
                      className="w-full"
                      size="lg"
                    >
                      تأكيد والبحث
                    </Button>
                    <Button
                      onClick={retakePhoto}
                      variant="outline"
                      className="w-full"
                    >
                      إعادة التصوير
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}